import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tour from '@/components/Tour';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity as DdosIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Network, 
  Plus, 
  Shield, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Globe,
  Webhook,
  User,
  LogOut,
  Clock,
  Mail,
  Copy,
  Check,
  RefreshCw,
  Settings,
  Lock,
  Eye,
  EyeOff,
  X,
  Info,
  Search,
  MapPin,
  BarChart3,
  Server,
  TrendingUp,
  Calendar,
  Filter
} from 'lucide-react';

// Simple Modal Component - Pure white, no shadows, no borders
const SimpleModal = ({ open, onClose, title, subtitle, children }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-lg p-6">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-6">
            {title && <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>}
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
          </div>
        )}
        
        {/* Content */}
        {children}
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { language } = useLanguage();
  const { user, isAuthenticated, isLoading: authLoading, logout, getASList, getAbuseContacts, addAS, removeAS, resendASVerification, getBGPList, addBGPSession, removeBGPSession, getWebhookList, addWebhook, removeWebhook, changePassword, changeEmailStep1, changeEmailStep2, checkDelistIP, requestDelist, getAdminStats, adminIpSearch } = useAuth();
  const navigate = useNavigate();

  // Tour State
  const [runTour, setRunTour] = useState(false);

  const tourSteps = [
    {
      target: '[data-tour="welcome"]',
      content: language === 'tr' 
        ? 'Threat.live Client Portal\'a hoş geldiniz! Bu kısa tur size paneli tanıtacak.' 
        : 'Welcome to Threat.live Client Portal! This quick tour will show you around.',
      placement: 'center',
    },
    {
      target: '[data-tour="tabs"]',
      content: language === 'tr' 
        ? 'Bu sekmelerden AS numaralarınızı, BGP oturumlarınızı, webhook\'larınızı ve ayarlarınızı yönetebilirsiniz.' 
        : 'Use these tabs to manage your AS numbers, BGP sessions, webhooks, and settings.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="as-tab"]',
      content: language === 'tr' 
        ? 'AS Yönetimi: Buradan AS numaralarınızı ekleyebilir ve RIPE abuse email doğrulaması yapabilirsiniz.' 
        : 'AS Management: Add your AS numbers here and verify them via RIPE abuse email.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="bgp-tab"]',
      content: language === 'tr' 
        ? 'BGP Oturumları: AS doğrulandıktan sonra BGP oturumlarınızı buradan oluşturabilirsiniz.' 
        : 'BGP Sessions: After AS verification, you can create your BGP sessions here.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="webhook-tab"]',
      content: language === 'tr' 
        ? 'Webhooks: Yeni IP bildirimleri almak için webhook URL\'lerinizi ekleyin.' 
        : 'Webhooks: Add your webhook URLs to receive new IP notifications.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="delist-tab"]',
      content: language === 'tr' 
        ? 'Delist: Listelenen bir IP adresinin kaldırılması için buradan talep oluşturabilirsiniz.' 
        : 'Delist: Request removal of a listed IP address from here.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="settings-tab"]',
      content: language === 'tr' 
        ? 'Ayarlar: Şifrenizi ve e-posta adresinizi buradan değiştirebilirsiniz.' 
        : 'Settings: Change your password and email address here.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="add-as-btn"]',
      content: language === 'tr' 
        ? 'Başlamak için "AS Ekle" butonuna tıklayarak ilk AS numaranızı ekleyin!' 
        : 'To get started, click "Add AS" to add your first AS number!',
      placement: 'left',
    },
  ];

  const handleTourFinish = () => {
    setRunTour(false);
    localStorage.setItem('threat_live_tour_completed', 'true');
  };

  // Check if tour should run on mount
  useEffect(() => {
    const tourCompleted = localStorage.getItem('threat_live_tour_completed');
    if (!tourCompleted && isAuthenticated && !authLoading) {
      // Small delay to ensure DOM is ready
      setTimeout(() => setRunTour(true), 500);
    }
  }, [isAuthenticated, authLoading]);

  const [activeTab, setActiveTab] = useState('as');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // AS State
  const [asList, setAsList] = useState([]);
  const [asModalOpen, setAsModalOpen] = useState(false);
  const [asStep, setAsStep] = useState(1);
  const [newAsn, setNewAsn] = useState('');
  const [abuseContacts, setAbuseContacts] = useState([]);
  const [selectedAbuseEmail, setSelectedAbuseEmail] = useState('');

  // BGP State
  const [bgpList, setBgpList] = useState([]);
  const [bgpModalOpen, setBgpModalOpen] = useState(false);
  const [selectedAsForBgp, setSelectedAsForBgp] = useState('');
  const [bgpForm, setBgpForm] = useState({
    session_name: '',
    remote_ip: '',
  });

  // Webhook State
  const [webhookList, setWebhookList] = useState([]);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(null);
  const [webhookDetailModal, setWebhookDetailModal] = useState({ open: false, webhook: null });

  // Delist State
  const [delistSearchIP, setDelistSearchIP] = useState('');
  const [delistResult, setDelistResult] = useState(null);
  const [delistReason, setDelistReason] = useState('');
  const [delistLoading, setDelistLoading] = useState(false);
  const [delistSuccess, setDelistSuccess] = useState('');

  // Profile/Settings State
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [emailStep, setEmailStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [emailToken, setEmailToken] = useState('');
  const [emailOtp, setEmailOtp] = useState('');

  // Admin: Liste bakımı + giden mailler
  const [pruneDays, setPruneDays] = useState(45);
  const [pruneLoading, setPruneLoading] = useState(false);
  const [prunePreview, setPrunePreview] = useState(null);
  const [pruneMsg, setPruneMsg] = useState('');
  const [delistIp, setDelistIp] = useState('');
  const [delistMsg, setDelistMsg] = useState('');
  const [mailLog, setMailLog] = useState(null);
  const [mailSearch, setMailSearch] = useState('');
  const [mailLoading, setMailLoading] = useState(false);
  const [ddosLog, setDdosLog] = useState(null);
  const [ddosSearch, setDdosSearch] = useState('');
  const [ddosSpoofed, setDdosSpoofed] = useState('');
  const [ddosProto, setDdosProto] = useState('');
  const [ddosLoading, setDdosLoading] = useState(false);

  // Admin Stats State
  const [adminStats, setAdminStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('24h');
  const [statsType, setStatsType] = useState('all');

  // Admin IP Search State
  const [ipSearchQuery, setIpSearchQuery] = useState('');
  const [ipSearchType, setIpSearchType] = useState('ip'); // 'ip' or 'prefix'
  const [ipSearchResults, setIpSearchResults] = useState(null);
  const [ipSearchLoading, setIpSearchLoading] = useState(false);

  // Admin IP Add State
  const [ipAddMode, setIpAddMode] = useState('single'); // 'single', 'bulk', 'items'
  const [ipAddSingleIp, setIpAddSingleIp] = useState('');
  const [ipAddSinglePorts, setIpAddSinglePorts] = useState('');
  const [ipAddBulkIps, setIpAddBulkIps] = useState('');
  const [ipAddBulkPorts, setIpAddBulkPorts] = useState('');
  const [ipAddItems, setIpAddItems] = useState('');
  const [ipAddLoading, setIpAddLoading] = useState(false);
  const [ipAddResult, setIpAddResult] = useState(null);
  const [ipAddError, setIpAddError] = useState(null);

  const translations = {
    tr: {
      dashboard: 'Dashboard',
      welcome: 'Hoş geldiniz',
      asManagement: 'AS Yönetimi',
      bgpManagement: 'BGP Oturumları',
      webhooks: 'Webhooks',
      profile: 'Profil',
      settings: 'Ayarlar',
      logout: 'Çıkış',
      // AS
      addAs: 'ASN Ekle',
      asNumber: 'AS Numarası',
      abuseEmail: 'E-posta',
      status: 'Durum',
      verified: 'Doğrulanmış',
      pending: 'Bekliyor',
      actions: 'İşlemler',
      noAs: 'Henüz AS eklenmemiş',
      step1Title: 'AS Numarası Girin',
      step1Desc: 'RIPE veritabanındaki contact bilgisi ile doğrulama yapmanız gereklidir.',
      step2Title: 'E-Posta Seçin',
      step2Desc: 'Doğrulama linki seçtiğiniz e-postaya gönderilecek',
      fetchContacts: 'Devam Et',
      sendVerification: 'Doğrulama Linki Gönder',
      verificationSent: 'Doğrulama linki e-postanıza gönderildi',
      maxSessions: 'Maks. Oturum',
      // BGP
      addBgp: 'BGP Oturumu Ekle',
      sessionName: 'Oturum Adı',
      localIp: 'Yerel IP',
      remoteIp: 'Remote IP',
      localAsn: 'Yerel ASN',
      clientAsn: 'Client ASN',
      remoteAsn: 'Uzak ASN',
      password: 'Şifre',
      optional: 'opsiyonel',
      selectAs: 'AS Seçin',
      noBgp: 'Henüz BGP oturumu eklenmemiş',
      asNotVerified: 'BGP Oturumu başlatabilmeniz için ASN doğrulamasını tamamlamanız gereklidir.',
      // Webhook
      addWebhook: 'Webhook Ekle',
      webhookUrl: 'Webhook URL',
      secretKey: 'Secret Key',
      secretKeyInfo: 'Güvenliğiniz için webhook bildirimlerinde HTTP header\'da "RTBH-SECRET-KEY" şeklinde bu anahtar gönderilecektir. Gelen isteklerin gerçekten Threat.live\'ten geldiğini doğrulamak için kullanabilirsiniz.',
      noWebhook: 'Henüz webhook eklenmemiş',
      webhookHttpsRequired: 'URL HTTPS olmalıdır',
      copySecret: 'Kopyala',
      copied: 'Kopyalandı!',
      lastTriggered: 'Son Tetiklenme',
      lastResponse: 'Son Yanıt',
      never: 'Henüz tetiklenmedi',
      viewDetails: 'Detay',
      webhookDetails: 'Webhook Detayları',
      // Delist
      delist: 'Delist',
      delistTitle: 'IP Delist Talebi',
      delistDescription: 'Listelenmiş bir IP adresini kaldırma talebinde bulunun',
      searchIP: 'IP Adresi Ara',
      searchIPPlaceholder: 'Örn: 192.168.1.1',
      search: 'Ara',
      ipFound: 'IP Adresi Bulundu',
      ipNotFound: 'IP Adresi Bulunamadı',
      ipNotFoundDesc: 'Bu IP adresi listelenmemektedir.',
      level: 'Seviye',
      ports: 'Portlar',
      country: 'Ülke',
      asn: 'ASN',
      org: 'Organizasyon',
      delistReason: 'Delist Sebebi',
      delistReasonPlaceholder: 'IP adresinin neden listeden çıkarılması gerektiğini açıklayın...',
      submitDelistRequest: 'Delist Talebi Gönder',
      delistRequestSent: 'Talebiniz alındı. En kısa sürede değerlendirilecektir.',
      // Settings
      accountSettings: 'Hesap Ayarları',
      changePassword: 'Şifre Değiştir',
      changeEmail: 'E-posta Değiştir',
      currentPassword: 'Mevcut Şifre',
      newPassword: 'Yeni Şifre',
      confirmNewPassword: 'Yeni Şifre (Tekrar)',
      updatePassword: 'Şifreyi Güncelle',
      passwordUpdated: 'Şifreniz başarıyla güncellendi',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordWeak: 'Şifre en az 8 karakter, büyük/küçük harf, rakam ve özel karakter içermeli',
      currentEmail: 'Mevcut E-posta',
      newEmailAddress: 'Yeni E-posta Adresi',
      sendOtp: 'Doğrulama Kodu Gönder',
      otpSent: 'Doğrulama kodu yeni e-posta adresinize gönderildi',
      enterOtp: 'Doğrulama Kodunu Girin',
      verifyAndUpdate: 'Doğrula ve Güncelle',
      emailUpdated: 'E-posta adresiniz başarıyla güncellendi',
      // Common
      cancel: 'İptal',
      create: 'Oluştur',
      delete: 'Sil',
      loading: 'Yükleniyor...',
      refresh: 'Yenile',
      back: 'Geri',
      updating: 'Güncelleniyor...',
      // Admin Stats
      statistics: 'İstatistikler',
      period: 'Periyot',
      last24h: 'Son 24 Saat',
      last7d: 'Son 7 Gün',
      last14d: 'Son 14 Gün',
      allTime: 'Tüm Zamanlar',
      byNode: 'Node Bazlı',
      byAsn: 'ASN Bazlı',
      byCountry: 'Ülke Bazlı',
      byPorts: 'Port Bazlı',
      byDate: 'Tarih Bazlı',
      summary: 'Özet',
      allStats: 'Tüm İstatistikler',
      totalIps: 'Toplam IP',
      activeIps: 'Aktif IP',
      deletedIps: 'Silinen IP',
      uniqueAsns: 'Benzersiz ASN',
      uniqueCountries: 'Benzersiz Ülke',
      node: 'Node',
      count: 'Adet',
      port: 'Port',
      date: 'Tarih',
      loadStats: 'İstatistikleri Yükle',
      noStatsData: 'İstatistik verisi bulunamadı',
      // Admin IP Search
      ipSearch: 'IP Arama',
      searchType: 'Arama Tipi',
      singleIp: 'Tek IP',
      prefixSearch: 'Prefix (CIDR)',
      ipPlaceholder: 'Örn: 192.168.1.1',
      prefixPlaceholder: 'Örn: 192.168.1.0/24',
      searchIp: 'Ara',
      searching: 'Aranıyor...',
      noResults: 'Sonuç bulunamadı',
      totalRecords: 'Toplam Kayıt',
      firstSeen: 'İlk Görülme',
      lastSeen: 'Son Görülme',
      nodeInfo: 'Node Bilgisi',
      ipAddress: 'IP Adresi',
      statusActive: 'Aktif',
      statusDeleted: 'Silindi',
      // Admin IP Add
      ipAdd: 'IP Ekle',
      ipAddDesc: 'Tehdit IP adreslerini sisteme ekleyin',
      addMode: 'Ekleme Modu',
      singleIpMode: 'Tek IP',
      bulkIpMode: 'Toplu IP (Ortak Port)',
      bulkItemsMode: 'Toplu IP (Ayrı Portlar)',
      ipAddressLabel: 'IP Adresi',
      portsLabel: 'Port(lar)',
      portsPlaceholder: 'Örn: TCP/22 veya TCP/80, TCP/443',
      ipsLabel: 'IP Adresleri (Her satıra bir IP)',
      ipsPlaceholder: '1.2.3.4\n5.6.7.8\n9.10.11.12',
      itemsLabel: 'IP ve Portlar (Her satıra: IP,Port)',
      itemsPlaceholder: '1.2.3.4,TCP/22\n5.6.7.8,TCP/80, TCP/443',
      addIps: 'IP Ekle',
      adding: 'Ekleniyor...',
      addSuccess: 'IP başarıyla eklendi',
      insertedRows: 'Eklenen Kayıt',
      updatedRows: 'Güncellenen Kayıt',
      affectedNodes: 'Etkilenen Node',
      uniqueIps: 'Benzersiz IP',
      addError: 'IP eklenirken hata oluştu',
    },
    en: {
      dashboard: 'Dashboard',
      welcome: 'Welcome',
      asManagement: 'AS Management',
      bgpManagement: 'BGP Sessions',
      webhooks: 'Webhooks',
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
      // AS
      addAs: 'Add ASN',
      asNumber: 'AS Number',
      abuseEmail: 'Email',
      status: 'Status',
      verified: 'Verified',
      pending: 'Pending',
      actions: 'Actions',
      noAs: 'No AS added yet',
      step1Title: 'Enter AS Number',
      step1Desc: 'Verification is required using the contact information in the RIPE database.',
      step2Title: 'Select Email',
      step2Desc: 'Verification link will be sent to the selected email',
      fetchContacts: 'Continue',
      sendVerification: 'Send Verification Link',
      verificationSent: 'Verification link sent to your email',
      maxSessions: 'Max Sessions',
      // BGP
      addBgp: 'Add BGP Session',
      sessionName: 'Session Name',
      localIp: 'Local IP',
      remoteIp: 'Remote IP',
      localAsn: 'Local ASN',
      clientAsn: 'Client ASN',
      remoteAsn: 'Remote ASN',
      password: 'Password',
      optional: 'optional',
      selectAs: 'Select AS',
      noBgp: 'No BGP sessions added yet',
      asNotVerified: 'ASN verification must be completed to start a BGP session.',
      // Webhook
      addWebhook: 'Add Webhook',
      webhookUrl: 'Webhook URL',
      secretKey: 'Secret Key',
      secretKeyInfo: 'For security, this key will be sent in the HTTP header as "RTBH-SECRET-KEY" with webhook notifications. You can use it to verify that incoming requests are genuinely from Threat.live.',
      noWebhook: 'No webhooks added yet',
      webhookHttpsRequired: 'URL must be HTTPS',
      copySecret: 'Copy',
      copied: 'Copied!',
      lastTriggered: 'Last Triggered',
      lastResponse: 'Last Response',
      never: 'Never triggered',
      viewDetails: 'Details',
      webhookDetails: 'Webhook Details',
      // Delist
      delist: 'Delist',
      delistTitle: 'IP Delist Request',
      delistDescription: 'Request removal of a listed IP address',
      searchIP: 'Search IP Address',
      searchIPPlaceholder: 'e.g., 192.168.1.1',
      search: 'Search',
      ipFound: 'IP Address Found',
      ipNotFound: 'IP Address Not Found',
      ipNotFoundDesc: 'This IP address is not listed.',
      level: 'Level',
      ports: 'Ports',
      country: 'Country',
      asn: 'ASN',
      org: 'Organization',
      delistReason: 'Delist Reason',
      delistReasonPlaceholder: 'Explain why this IP address should be removed from the list...',
      submitDelistRequest: 'Submit Delist Request',
      delistRequestSent: 'Your request has been received. It will be reviewed shortly.',
      // Settings
      accountSettings: 'Account Settings',
      changePassword: 'Change Password',
      changeEmail: 'Change Email',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      updatePassword: 'Update Password',
      passwordUpdated: 'Your password has been updated successfully',
      passwordMismatch: 'Passwords do not match',
      passwordWeak: 'Password must be at least 8 characters with uppercase, lowercase, number and special character',
      currentEmail: 'Current Email',
      newEmailAddress: 'New Email Address',
      sendOtp: 'Send Verification Code',
      otpSent: 'Verification code sent to your new email address',
      enterOtp: 'Enter Verification Code',
      verifyAndUpdate: 'Verify and Update',
      emailUpdated: 'Your email has been updated successfully',
      // Common
      cancel: 'Cancel',
      create: 'Create',
      delete: 'Delete',
      loading: 'Loading...',
      refresh: 'Refresh',
      back: 'Back',
      updating: 'Updating...',
      // Admin Stats
      statistics: 'Statistics',
      period: 'Period',
      last24h: 'Last 24 Hours',
      last7d: 'Last 7 Days',
      last14d: 'Last 14 Days',
      allTime: 'All Time',
      byNode: 'By Node',
      byAsn: 'By ASN',
      byCountry: 'By Country',
      byPorts: 'By Ports',
      byDate: 'By Date',
      summary: 'Summary',
      allStats: 'All Statistics',
      totalIps: 'Total IPs',
      activeIps: 'Active IPs',
      deletedIps: 'Deleted IPs',
      uniqueAsns: 'Unique ASNs',
      uniqueCountries: 'Unique Countries',
      node: 'Node',
      count: 'Count',
      port: 'Port',
      date: 'Date',
      loadStats: 'Load Statistics',
      noStatsData: 'No statistics data found',
      // Admin IP Search
      ipSearch: 'IP Search',
      searchType: 'Search Type',
      singleIp: 'Single IP',
      prefixSearch: 'Prefix (CIDR)',
      ipPlaceholder: 'e.g., 192.168.1.1',
      prefixPlaceholder: 'e.g., 192.168.1.0/24',
      searchIp: 'Search',
      searching: 'Searching...',
      noResults: 'No results found',
      totalRecords: 'Total Records',
      firstSeen: 'First Seen',
      lastSeen: 'Last Seen',
      nodeInfo: 'Node Info',
      ipAddress: 'IP Address',
      statusActive: 'Active',
      statusDeleted: 'Deleted',
      // Admin IP Add
      ipAdd: 'Add IP',
      ipAddDesc: 'Add threat IP addresses to the system',
      addMode: 'Add Mode',
      singleIpMode: 'Single IP',
      bulkIpMode: 'Bulk IPs (Common Port)',
      bulkItemsMode: 'Bulk IPs (Individual Ports)',
      ipAddressLabel: 'IP Address',
      portsLabel: 'Port(s)',
      portsPlaceholder: 'e.g., TCP/22 or TCP/80, TCP/443',
      ipsLabel: 'IP Addresses (One per line)',
      ipsPlaceholder: '1.2.3.4\n5.6.7.8\n9.10.11.12',
      itemsLabel: 'IPs and Ports (Per line: IP,Port)',
      itemsPlaceholder: '1.2.3.4,TCP/22\n5.6.7.8,TCP/80, TCP/443',
      addIps: 'Add IPs',
      adding: 'Adding...',
      addSuccess: 'IPs added successfully',
      insertedRows: 'Inserted Rows',
      updatedRows: 'Updated Rows',
      affectedNodes: 'Affected Nodes',
      uniqueIps: 'Unique IPs',
      addError: 'Error adding IPs',
    }
  };

  const txt = translations[language] || translations.en;

  // Redirect if not authenticated (wait for auth loading to complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch data when authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchAllData();
    }
  }, [authLoading, isAuthenticated]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [asData, bgpData, webhookData] = await Promise.all([
        getASList().catch(() => ({ asns: [] })),
        getBGPList().catch(() => ({ sessions: [] })),
        getWebhookList().catch(() => ({ webhooks: [] })),
      ]);
      setAsList(asData.asns || []);
      setBgpList(bgpData.sessions || []);
      setWebhookList(webhookData.webhooks || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Admin Stats Functions
  const fetchAdminStats = async () => {
    setStatsLoading(true);
    setError('');
    try {
      const data = await getAdminStats({
        period: statsPeriod,
        type: statsType,
        limit: 100
      });
      if (data.status === 'success') {
        setAdminStats(data.data);
      } else {
        setError(data.message || 'Failed to load statistics');
      }
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  // Admin IP Search Function
  const handleIpSearch = async () => {
    if (!ipSearchQuery.trim()) return;
    
    setIpSearchLoading(true);
    setError('');
    setIpSearchResults(null);
    
    try {
      const searchData = ipSearchType === 'ip' 
        ? { ip: ipSearchQuery.trim() }
        : { prefix: ipSearchQuery.trim() };
      
      const data = await adminIpSearch(searchData);
      
      if (data.status === 'success') {
        setIpSearchResults(data);
      } else {
        setError(data.message || 'Search failed');
      }
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setIpSearchLoading(false);
    }
  };

  // Admin IP Add Function
  const handleIpAdd = async () => {
    setIpAddLoading(true);
    setIpAddError(null);
    setIpAddResult(null);
    
    try {
      let requestBody = {};
      
      if (ipAddMode === 'single') {
        // Single IP mode
        if (!ipAddSingleIp.trim() || !ipAddSinglePorts.trim()) {
          setIpAddError(language === 'tr' ? 'IP ve Port alanları zorunludur' : 'IP and Port fields are required');
          setIpAddLoading(false);
          return;
        }
        requestBody = {
          ip: ipAddSingleIp.trim(),
          ports: ipAddSinglePorts.trim()
        };
      } else if (ipAddMode === 'bulk') {
        // Bulk IPs with common port
        if (!ipAddBulkIps.trim() || !ipAddBulkPorts.trim()) {
          setIpAddError(language === 'tr' ? 'IP listesi ve Port alanları zorunludur' : 'IP list and Port fields are required');
          setIpAddLoading(false);
          return;
        }
        const ips = ipAddBulkIps.trim().split('\n').map(ip => ip.trim()).filter(ip => ip);
        requestBody = {
          ips: ips,
          ports: ipAddBulkPorts.trim()
        };
      } else if (ipAddMode === 'items') {
        // Bulk with individual ports
        if (!ipAddItems.trim()) {
          setIpAddError(language === 'tr' ? 'IP ve Port listesi zorunludur' : 'IP and Port list is required');
          setIpAddLoading(false);
          return;
        }
        const lines = ipAddItems.trim().split('\n').filter(line => line.trim());
        const items = lines.map(line => {
          const [ip, ...portsParts] = line.split(',');
          return {
            ip: ip.trim(),
            ports: portsParts.join(',').trim()
          };
        }).filter(item => item.ip && item.ports);
        
        if (items.length === 0) {
          setIpAddError(language === 'tr' ? 'Geçerli IP,Port formatı bulunamadı' : 'No valid IP,Port format found');
          setIpAddLoading(false);
          return;
        }
        requestBody = { items };
      }
      
      const response = await fetch('https://api.threat.live/client/admin/threat-ips-import', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setIpAddResult(data);
        // Clear form
        setIpAddSingleIp('');
        setIpAddSinglePorts('');
        setIpAddBulkIps('');
        setIpAddBulkPorts('');
        setIpAddItems('');
      } else {
        setIpAddError(data.message || (language === 'tr' ? 'IP eklenirken hata oluştu' : 'Error adding IPs'));
      }
    } catch (err) {
      setIpAddError(err.message || (language === 'tr' ? 'IP eklenirken hata oluştu' : 'Error adding IPs'));
    } finally {
      setIpAddLoading(false);
    }
  };

  // Admin: Liste bakımı & giden mailler
  const ADMIN_BASE = 'https://api.threat.live/client/admin';

  const handlePrunePreview = async () => {
    setPruneLoading(true); setPruneMsg(''); setPrunePreview(null);
    try {
      const res = await fetch(`${ADMIN_BASE}/prune-preview.php`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: Number(pruneDays) || 30 }),
      });
      const data = await res.json();
      if (data.status === 'ok') setPrunePreview(data);
      else setPruneMsg(data.message || 'Hata');
    } catch (e) { setPruneMsg(e.message || 'Hata'); }
    finally { setPruneLoading(false); }
  };

  const handlePruneExecute = async () => {
    if (!prunePreview) return;
    const ok = window.confirm(language === 'tr'
      ? `${prunePreview.total} IP listeden düşürülecek (status=2). Emin misiniz?`
      : `${prunePreview.total} IPs will be delisted (status=2). Are you sure?`);
    if (!ok) return;
    setPruneLoading(true); setPruneMsg('');
    try {
      const res = await fetch(`${ADMIN_BASE}/prune-execute.php`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: Number(pruneDays) || 30 }),
      });
      const data = await res.json();
      if (data.status === 'ok') {
        setPruneMsg(language === 'tr'
          ? `${data.unique_ips} IP düşürüldü (${data.rows_updated} satır).`
          : `${data.unique_ips} IPs delisted (${data.rows_updated} rows).`);
        setPrunePreview(null);
      } else setPruneMsg(data.message || 'Hata');
    } catch (e) { setPruneMsg(e.message || 'Hata'); }
    finally { setPruneLoading(false); }
  };

  const handleDelistIp = async () => {
    if (!delistIp.trim()) return;
    setDelistMsg('');
    try {
      const res = await fetch(`${ADMIN_BASE}/delist-ip.php`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: delistIp.trim() }),
      });
      const data = await res.json();
      setDelistMsg(data.message || (data.status === 'ok' ? 'OK' : 'Hata'));
      if (data.status === 'ok') setDelistIp('');
    } catch (e) { setDelistMsg(e.message || 'Hata'); }
  };

  const loadMailLog = async (page = 1) => {
    setMailLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), per_page: '25', search: mailSearch });
      const res = await fetch(`${ADMIN_BASE}/mail-log.php?${qs.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (data.status === 'ok') setMailLog(data);
    } catch (e) { /* yut */ }
    finally { setMailLoading(false); }
  };

  const loadDdos = async (page = 1) => {
    setDdosLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), per_page: '25', search: ddosSearch });
      if (ddosSpoofed !== '') qs.set('spoofed', ddosSpoofed);
      if (ddosProto !== '') qs.set('proto', ddosProto);
      const res = await fetch(`${ADMIN_BASE}/ddos-events.php?${qs.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (data.status === 'ok') setDdosLog(data);
    } catch (e) { /* yut */ }
    finally { setDdosLoading(false); }
  };

  // AS Functions
  const handleFetchAbuseContacts = async () => {
    if (!newAsn) return;
    setIsLoading(true);
    setError('');
    try {
      // Prepend AS to the number before sending to backend
      const asnWithPrefix = `AS${newAsn}`;
      const data = await getAbuseContacts(asnWithPrefix);
      if (data.status === 'success') {
        setAbuseContacts(data.abuse_contacts || []);
        setAsStep(2);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAS = async () => {
    if (!selectedAbuseEmail) return;
    setIsLoading(true);
    setError('');
    try {
      // Prepend AS to the number before sending to backend
      const asnWithPrefix = `AS${newAsn}`;
      const data = await addAS(asnWithPrefix, selectedAbuseEmail);
      if (data.status === 'success') {
        setSuccess(txt.verificationSent);
        setAsModalOpen(false);
        resetAsModal();
        fetchAllData();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAS = async (asnId) => {
    if (!confirm('Are you sure?')) return;
    try {
      await removeAS(asnId);
      fetchAllData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResendVerification = async (asnId) => {
    setIsLoading(true);
    setError('');
    try {
      const data = await resendASVerification(asnId);
      if (data.status === 'success') {
        setSuccess(language === 'tr' ? 'Doğrulama maili tekrar gönderildi' : 'Verification email resent');
        fetchAllData();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAsModal = () => {
    setAsStep(1);
    setNewAsn('');
    setAbuseContacts([]);
    setSelectedAbuseEmail('');
    setError('');
  };

  // BGP Validation Functions
  const isValidSessionName = (name) => {
    // Letters, numbers, dash, underscore, dot and spaces allowed (no Turkish characters)
    return /^[a-zA-Z0-9\s\-_.]+$/.test(name);
  };

  const isValidIP = (ip) => {
    // Valid IPv4 format
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleSessionNameChange = (e) => {
    const value = e.target.value;
    // Only allow English letters, numbers, dash, underscore, dot and spaces (no Turkish characters)
    // Max 17 characters
    const filtered = value.replace(/[^a-zA-Z0-9\s\-_.]/g, '').slice(0, 17);
    setBgpForm({...bgpForm, session_name: filtered});
  };

  const handleRemoteIPChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and dots
    const filtered = value.replace(/[^0-9.]/g, '');
    setBgpForm({...bgpForm, remote_ip: filtered});
  };

  // BGP Functions
  const handleAddBGP = async () => {
    // Validation before submit
    if (!isValidSessionName(bgpForm.session_name)) {
      setError(language === 'tr' 
        ? 'Oturum adı sadece harf, rakam, tire (-), alt çizgi (_), nokta (.) ve boşluk içerebilir' 
        : 'Session name can only contain letters, numbers, dash (-), underscore (_), dot (.) and spaces');
      return;
    }
    
    if (!isValidIP(bgpForm.remote_ip)) {
      setError(language === 'tr' 
        ? 'Geçerli bir IP adresi girin (örn: 192.168.1.100)' 
        : 'Enter a valid IP address (e.g., 192.168.1.100)');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await addBGPSession({
        asn_id: selectedAsForBgp,
        ...bgpForm,
      });
      if (data.status === 'success') {
        setBgpModalOpen(false);
        resetBgpModal();
        fetchAllData();
      } else {
        // Handle errors array if present
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          setError(data.errors.join('\n'));
        } else {
          setError(data.message || 'Bir hata oluştu');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBGP = async (bgpId) => {
    if (!confirm('Are you sure?')) return;
    try {
      await removeBGPSession(bgpId);
      fetchAllData();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetBgpModal = () => {
    setSelectedAsForBgp('');
    setBgpForm({ session_name: '', remote_ip: '' });
    setError('');
  };

  // Webhook Functions
  const handleAddWebhook = async () => {
    if (!newWebhookUrl.startsWith('https://')) {
      setError(txt.webhookHttpsRequired);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const data = await addWebhook(newWebhookUrl);
      if (data.status === 'success') {
        setWebhookModalOpen(false);
        setNewWebhookUrl('');
        fetchAllData();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveWebhook = async (webhookId) => {
    if (!confirm('Are you sure?')) return;
    try {
      await removeWebhook(webhookId);
      fetchAllData();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedSecret(id);
    setTimeout(() => setCopiedSecret(null), 2000);
  };

  // Delist Functions
  const handleDelistSearch = async () => {
    if (!delistSearchIP.trim()) return;
    
    setDelistLoading(true);
    setError('');
    setDelistSuccess('');
    setDelistResult(null);
    
    try {
      const data = await checkDelistIP(delistSearchIP.trim());
      setDelistResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDelistLoading(false);
    }
  };

  const handleDelistRequest = async () => {
    if (!delistResult?.found || !delistReason.trim()) return;
    
    setDelistLoading(true);
    setError('');
    
    try {
      const data = await requestDelist({
        ip: delistResult.ip,
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        email: user?.email,
        reason: delistReason.trim(),
        company: user?.company_name || undefined,
      });
      
      if (data.status === 'success') {
        setDelistSuccess(txt.delistRequestSent);
        setDelistReason('');
        setDelistResult(null);
        setDelistSearchIP('');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDelistLoading(false);
    }
  };

  // Password Functions
  const validatePassword = (password) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError(txt.passwordMismatch);
      return;
    }
    if (!validatePassword(passwordForm.new_password)) {
      setError(txt.passwordWeak);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const data = await changePassword(passwordForm.current_password, passwordForm.new_password);
      if (data.status === 'success') {
        setSuccess(txt.passwordUpdated);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Email Change Functions
  const handleChangeEmailStep1 = async () => {
    if (!newEmail) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await changeEmailStep1(newEmail);
      if (data.status === 'success') {
        setEmailToken(data.token);
        setEmailStep(2);
        setSuccess(txt.otpSent);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmailStep2 = async () => {
    if (!emailOtp) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await changeEmailStep2(emailToken, emailOtp);
      if (data.status === 'success') {
        setSuccess(txt.emailUpdated);
        setEmailStep(1);
        setNewEmail('');
        setEmailOtp('');
        setEmailToken('');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // If auth is still loading, show loading screen
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-muted-foreground">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // If not authenticated after loading, don't render (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  const verifiedAsList = asList.filter(as => as.verification_status === 'verified');

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page" data-tour="welcome">
      <SEO page="dashboard" />
      <Header />
      
      {/* Tour */}
      <Tour
        steps={tourSteps}
        run={runTour}
        onFinish={handleTourFinish}
        language={language}
      />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{txt.dashboard}</h1>
            <p className="text-muted-foreground">
              {txt.welcome}, {user?.first_name} {user?.last_name}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={fetchAllData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {txt.refresh}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              {txt.logout}
            </Button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <Alert className="mb-6 bg-emerald-500/10 border-emerald-500/30">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <AlertDescription className="text-emerald-600 dark:text-emerald-400">{success}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setError(''); setSuccess(''); }}>
          <TabsList className="mb-6 gap-1" data-tour="tabs">
            <TabsTrigger value="as" className="gap-2" data-tour="as-tab">
              <Globe className="h-4 w-4" />
              {txt.asManagement}
            </TabsTrigger>
            <TabsTrigger value="bgp" className="gap-2" data-tour="bgp-tab">
              <Network className="h-4 w-4" />
              {txt.bgpManagement}
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="gap-2" data-tour="webhook-tab">
              <Webhook className="h-4 w-4" />
              {txt.webhooks}
            </TabsTrigger>
            <TabsTrigger value="delist" className="gap-2" data-tour="delist-tab">
              <Search className="h-4 w-4" />
              {txt.delist}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-tour="settings-tab">
              <Settings className="h-4 w-4" />
              {txt.settings}
            </TabsTrigger>
            {user?.admin === 1 && (
              <TabsTrigger value="stats" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                {txt.statistics}
              </TabsTrigger>
            )}
            {user?.admin === 1 && (
              <TabsTrigger value="ipsearch" className="gap-2">
                <Search className="h-4 w-4" />
                {txt.ipSearch}
              </TabsTrigger>
            )}
            {user?.admin === 1 && (
              <TabsTrigger value="ipadd" className="gap-2">
                <Plus className="h-4 w-4" />
                {txt.ipAdd}
              </TabsTrigger>
            )}
            {user?.admin === 1 && (
              <TabsTrigger value="maint" className="gap-2">
                <Trash2 className="h-4 w-4" />
                {language === 'tr' ? 'Liste Bakımı' : 'List Cleanup'}
              </TabsTrigger>
            )}
            {user?.admin === 1 && (
              <TabsTrigger value="sentmail" className="gap-2" onClick={() => loadMailLog(1)}>
                <Mail className="h-4 w-4" />
                {language === 'tr' ? 'Giden Mailler' : 'Sent Mails'}
              </TabsTrigger>
            )}
            {user?.admin === 1 && (
              <TabsTrigger value="ddos" className="gap-2" onClick={() => loadDdos(1)}>
                <DdosIcon className="h-4 w-4" />
                Spoof / DDoS
              </TabsTrigger>
            )}
          </TabsList>

          {/* AS Tab */}
          <TabsContent value="as">
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{txt.asManagement}</CardTitle>
                  <CardDescription>Autonomous System yönetimi</CardDescription>
                </div>
                <Button onClick={() => setAsModalOpen(true)} className="bg-gradient-to-r from-emerald-500 to-cyan-500" data-tour="add-as-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {txt.addAs}
                </Button>
              </CardHeader>
              <CardContent>
                {asList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{txt.noAs}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{txt.asNumber}</TableHead>
                        <TableHead>{txt.abuseEmail}</TableHead>
                        <TableHead>{txt.status}</TableHead>
                        <TableHead>BGP / {txt.maxSessions}</TableHead>
                        <TableHead className="text-right">{txt.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {asList.map((as) => (
                        <TableRow key={as.asn_id}>
                          <TableCell className="font-mono font-bold">{as.asn}</TableCell>
                          <TableCell>{as.abuse_email}</TableCell>
                          <TableCell>
                            {as.verification_status === 'verified' ? (
                              <Badge className="bg-emerald-500/20 text-emerald-500">{txt.verified}</Badge>
                            ) : (
                              <Badge className="bg-yellow-500/20 text-yellow-500">{txt.pending}</Badge>
                            )}
                          </TableCell>
                          <TableCell>{as.bgp_sessions_count} / {as.max_sessions}</TableCell>
                          <TableCell className="text-right flex items-center justify-end gap-1">
                            {as.verification_status !== 'verified' && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="text-blue-500 hover:text-blue-600"
                                      onClick={() => handleResendVerification(as.asn_id)}
                                      disabled={!as.can_resend_email}
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {as.can_resend_email 
                                      ? (language === 'tr' ? 'Doğrulama mailini tekrar gönder' : 'Resend verification email')
                                      : (language === 'tr' ? `${as.resend_cooldown_minutes || 15} dakika bekleyin` : `Wait ${as.resend_cooldown_minutes || 15} minutes`)
                                    }
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleRemoveAS(as.asn_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BGP Tab */}
          <TabsContent value="bgp">
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{txt.bgpManagement}</CardTitle>
                  <CardDescription>BGP oturum yönetimi</CardDescription>
                </div>
                <Button 
                  onClick={() => setBgpModalOpen(true)} 
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                  disabled={verifiedAsList.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {txt.addBgp}
                </Button>
              </CardHeader>
              <CardContent>
                {verifiedAsList.length === 0 && (
                  <Alert className="mb-4 bg-yellow-500/10 border-yellow-500/30">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <AlertDescription className="text-yellow-600 dark:text-yellow-400">{txt.asNotVerified}</AlertDescription>
                  </Alert>
                )}
                {bgpList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{txt.noBgp}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{txt.sessionName}</TableHead>
                        <TableHead>{txt.localAsn}</TableHead>
                        <TableHead>{txt.localIp}</TableHead>
                        <TableHead>{txt.remoteIp}</TableHead>
                        <TableHead>{txt.remoteAsn}</TableHead>
                        <TableHead className="text-right">{txt.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bgpList.map((session) => (
                        <TableRow key={session.bgp_id}>
                          <TableCell className="font-medium">{session.session_name}</TableCell>
                          <TableCell className="font-mono">{session.local_asn}</TableCell>
                          <TableCell className="font-mono">{session.local_ip}</TableCell>
                          <TableCell className="font-mono">{session.remote_ip}</TableCell>
                          <TableCell className="font-mono">{session.remote_asn}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleRemoveBGP(session.bgp_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks">
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{txt.webhooks}</CardTitle>
                  <CardDescription>Webhook entegrasyonları</CardDescription>
                </div>
                <Button onClick={() => setWebhookModalOpen(true)} className="bg-gradient-to-r from-emerald-500 to-cyan-500">
                  <Plus className="h-4 w-4 mr-2" />
                  {txt.addWebhook}
                </Button>
              </CardHeader>
              <CardContent>
                {webhookList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Webhook className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{txt.noWebhook}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{txt.webhookUrl}</TableHead>
                        <TableHead>
                          <div className="flex items-center gap-1">
                            {txt.secretKey}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                    <Info className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm">{txt.secretKeyInfo}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableHead>
                        <TableHead>{txt.lastTriggered}</TableHead>
                        <TableHead className="text-right">{txt.actions}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {webhookList.map((webhook) => (
                        <TableRow key={webhook.webhook_id}>
                          <TableCell className="font-mono text-sm max-w-[200px] truncate">{webhook.webhook_url}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-muted px-2 py-1 rounded">{webhook.secret_key?.slice(0, 20)}...</code>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-6 w-6"
                                onClick={() => copyToClipboard(webhook.secret_key, webhook.webhook_id)}
                              >
                                {copiedSecret === webhook.webhook_id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {webhook.last_triggered_at || txt.never}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {webhook.last_response && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-blue-500 hover:text-blue-600 h-8 px-2"
                                  onClick={() => setWebhookDetailModal({ open: true, webhook })}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  {txt.viewDetails}
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleRemoveWebhook(webhook.webhook_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Delist Tab */}
          <TabsContent value="delist">
            <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
              <CardHeader>
                <div>
                  <CardTitle>{txt.delistTitle}</CardTitle>
                  <CardDescription>{txt.delistDescription}</CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search Box */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder={txt.searchIPPlaceholder}
                      value={delistSearchIP}
                      onChange={(e) => setDelistSearchIP(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDelistSearch()}
                      className="font-mono"
                      data-testid="delist-search-input"
                    />
                  </div>
                  <Button 
                    onClick={handleDelistSearch} 
                    disabled={delistLoading || !delistSearchIP.trim()}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                  >
                    {delistLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        {txt.search}
                      </>
                    )}
                  </Button>
                </div>

                {/* Success Message */}
                {delistSuccess && (
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <AlertDescription className="text-emerald-600 dark:text-emerald-400">
                      {delistSuccess}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Error Message */}
                {error && activeTab === 'delist' && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Search Result - IP Not Found */}
                {delistResult && !delistResult.found && (
                  <div className="text-center py-12 bg-muted/30 rounded-lg">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <h3 className="text-lg font-semibold mb-2">{txt.ipNotFound}</h3>
                    <p className="text-muted-foreground">{txt.ipNotFoundDesc}</p>
                    <p className="text-sm font-mono mt-2 text-muted-foreground">{delistResult.ip}</p>
                  </div>
                )}

                {/* Search Result - IP Found */}
                {delistResult && delistResult.found && (
                  <div className="space-y-6">
                    {/* IP Info Card */}
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5" />
                            {txt.ipFound}
                          </h3>
                          <p className="text-2xl font-mono font-bold mt-1">{delistResult.ip}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-muted-foreground">{txt.level}</span>
                          <div className={`text-2xl font-bold ${
                            delistResult.level === 1 ? 'text-yellow-500' : 
                            delistResult.level === 2 ? 'text-orange-500' : 
                            'text-red-500'
                          }`}>
                            {delistResult.level}
                          </div>
                        </div>
                      </div>

                      {/* Details Grid */}
                      {delistResult.details && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-red-500/20">
                          {delistResult.details.ports && (
                            <div>
                              <span className="text-xs text-muted-foreground uppercase">{txt.ports}</span>
                              <p className="font-mono text-sm">{delistResult.details.ports}</p>
                            </div>
                          )}
                          {delistResult.details.asn && (
                            <div>
                              <span className="text-xs text-muted-foreground uppercase">{txt.asn}</span>
                              <p className="font-mono text-sm">{delistResult.details.asn}</p>
                            </div>
                          )}
                          {delistResult.details.country && (
                            <div>
                              <span className="text-xs text-muted-foreground uppercase">{txt.country}</span>
                              <p className="text-sm flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {delistResult.details.country}
                              </p>
                            </div>
                          )}
                          {delistResult.details.org && (
                            <div>
                              <span className="text-xs text-muted-foreground uppercase">{txt.org}</span>
                              <p className="text-sm truncate">{delistResult.details.org}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Delist Request Form */}
                    <div className="space-y-4">
                      <div>
                        <Label>{txt.delistReason}</Label>
                        <textarea
                          className="w-full mt-2 px-3 py-2 bg-background border border-input rounded-md text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder={txt.delistReasonPlaceholder}
                          value={delistReason}
                          onChange={(e) => setDelistReason(e.target.value)}
                          data-testid="delist-reason-input"
                        />
                      </div>
                      <Button 
                        onClick={handleDelistRequest}
                        disabled={delistLoading || !delistReason.trim()}
                        className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600"
                      >
                        {delistLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        {txt.submitDelistRequest}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!delistResult && !delistSuccess && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{language === 'tr' ? 'Kontrol etmek istediğiniz IP adresini girin' : 'Enter the IP address you want to check'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Change Password */}
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    {txt.changePassword}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>{txt.currentPassword}</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.newPassword}</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{txt.confirmNewPassword}</Label>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={isLoading || !passwordForm.current_password || !passwordForm.new_password}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                    data-testid="update-password-btn"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {txt.updatePassword}
                  </Button>
                </CardContent>
              </Card>

              {/* Change Email */}
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    {txt.changeEmail}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>{txt.currentEmail}</Label>
                    <Input value={user?.email || ''} disabled className="bg-muted" />
                  </div>

                  {emailStep === 1 ? (
                    <>
                      <div className="space-y-2">
                        <Label>{txt.newEmailAddress}</Label>
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="new@example.com"
                          data-testid="new-email-input"
                        />
                      </div>
                      <Button 
                        onClick={handleChangeEmailStep1} 
                        disabled={isLoading || !newEmail}
                        className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                        data-testid="send-email-otp-btn"
                      >
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        {txt.sendOtp}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert className="bg-emerald-500/10 border-emerald-500/30">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <AlertDescription className="text-emerald-600 dark:text-emerald-400">
                          {txt.otpSent}
                        </AlertDescription>
                      </Alert>
                      <div className="space-y-2">
                        <Label>{txt.enterOtp}</Label>
                        <Input
                          type="text"
                          value={emailOtp}
                          onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="123456"
                          className="text-center text-xl tracking-widest"
                          maxLength={6}
                          data-testid="email-otp-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => { setEmailStep(1); setEmailOtp(''); }}
                          className="flex-1"
                        >
                          {txt.back}
                        </Button>
                        <Button 
                          onClick={handleChangeEmailStep2} 
                          disabled={isLoading || emailOtp.length !== 6}
                          className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500"
                          data-testid="verify-email-btn"
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          {txt.verifyAndUpdate}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Statistics Tab - Admin Only */}
          {user?.admin === 1 && (
            <TabsContent value="stats">
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      {txt.statistics}
                    </CardTitle>
                    <CardDescription>
                      {language === 'tr' ? 'Threat.live istatistiklerini görüntüle' : 'View Threat.live statistics'}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <Select value={statsPeriod} onValueChange={setStatsPeriod}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder={txt.period} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24h">{txt.last24h}</SelectItem>
                          <SelectItem value="7d">{txt.last7d}</SelectItem>
                          <SelectItem value="14d">{txt.last14d}</SelectItem>
                          <SelectItem value="all">{txt.allTime}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <Select value={statsType} onValueChange={setStatsType}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{txt.allStats}</SelectItem>
                          <SelectItem value="summary">{txt.summary}</SelectItem>
                          <SelectItem value="by_node">{txt.byNode}</SelectItem>
                          <SelectItem value="by_asn">{txt.byAsn}</SelectItem>
                          <SelectItem value="by_country">{txt.byCountry}</SelectItem>
                          <SelectItem value="by_ports">{txt.byPorts}</SelectItem>
                          <SelectItem value="by_date">{txt.byDate}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button 
                      onClick={fetchAdminStats} 
                      disabled={statsLoading}
                      className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                    >
                      {statsLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      {txt.loadStats}
                    </Button>
                  </div>

                  {/* Error Message */}
                  {error && activeTab === 'stats' && (
                    <Alert className="mb-6 bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <AlertDescription className="text-red-600 dark:text-red-400">{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Stats Content */}
                  {statsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                    </div>
                  ) : adminStats ? (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      {adminStats.summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                          <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border-emerald-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-emerald-600">{adminStats.summary.total_ips?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">{txt.totalIps}</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-blue-600">{adminStats.summary.active_ips?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">{txt.activeIps}</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-red-600">{adminStats.summary.deleted_ips?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">{txt.deletedIps}</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-purple-600">{adminStats.summary.total_asns?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">{txt.uniqueAsns}</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-amber-600">{adminStats.summary.total_countries?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">{txt.uniqueCountries}</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border-cyan-500/20">
                            <CardContent className="pt-4">
                              <div className="text-2xl font-bold text-cyan-600">{adminStats.summary.total_nodes?.toLocaleString() || 0}</div>
                              <div className="text-xs text-muted-foreground">Node</div>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-slate-500/10 to-gray-500/10 border-slate-500/20">
                            <CardContent className="pt-4">
                              <div className="text-sm font-mono text-slate-600">{adminStats.summary.last_record?.split(' ')[0] || '-'}</div>
                              <div className="text-xs text-muted-foreground">{language === 'tr' ? 'Son Kayıt' : 'Last Record'}</div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* By Node Table */}
                        {adminStats.by_node && adminStats.by_node.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Server className="h-4 w-4" />
                                {txt.byNode}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>{txt.node}</TableHead>
                                    <TableHead>{language === 'tr' ? 'Şirket' : 'Company'}</TableHead>
                                    <TableHead>{language === 'tr' ? 'Şehir' : 'City'}</TableHead>
                                    <TableHead className="text-right">IP</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {adminStats.by_node.map((item, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium">{item.node_name}</TableCell>
                                      <TableCell className="text-muted-foreground">{item.company}</TableCell>
                                      <TableCell className="text-muted-foreground">{item.city}, {item.node_country}</TableCell>
                                      <TableCell className="text-right font-mono font-bold">{item.ip_count?.toLocaleString()}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        )}

                        {/* By Country Table */}
                        {adminStats.by_country && adminStats.by_country.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {txt.byCountry}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="max-h-[400px] overflow-y-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>{txt.country}</TableHead>
                                      <TableHead className="text-right">IP</TableHead>
                                      <TableHead className="text-right">ASN</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {adminStats.by_country.slice(0, 20).map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">
                                          <span className="mr-2">{item.iso_code ? String.fromCodePoint(...item.iso_code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0))) : '🌐'}</span>
                                          {item.country}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">{item.ip_count?.toLocaleString()}</TableCell>
                                        <TableCell className="text-right font-mono text-muted-foreground">{item.asn_count}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>

                      {/* By ASN Table - Full Width */}
                      {adminStats.by_asn && adminStats.by_asn.length > 0 && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Globe className="h-4 w-4" />
                              {txt.byAsn}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="max-h-[400px] overflow-y-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>ASN</TableHead>
                                    <TableHead>{language === 'tr' ? 'Organizasyon' : 'Organization'}</TableHead>
                                    <TableHead>{language === 'tr' ? 'Ülkeler' : 'Countries'}</TableHead>
                                    <TableHead className="text-right">IP</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {adminStats.by_asn.slice(0, 30).map((item, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-mono">
                                        <a 
                                          href={`https://bgp.tools/as/${item.asn}`} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-emerald-600 hover:underline"
                                        >
                                          AS{item.asn}
                                        </a>
                                      </TableCell>
                                      <TableCell className="max-w-[300px] truncate" title={item.org}>{item.org}</TableCell>
                                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate" title={item.countries}>
                                        {item.iso_codes}
                                      </TableCell>
                                      <TableCell className="text-right font-mono font-bold">{item.ip_count?.toLocaleString()}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* By Ports Table */}
                        {adminStats.by_ports && adminStats.by_ports.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                {txt.byPorts}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="max-h-[300px] overflow-y-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>{txt.port}</TableHead>
                                      <TableHead className="text-right">{txt.count}</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {adminStats.by_ports.slice(0, 20).map((item, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-mono text-sm">{item.ports}</TableCell>
                                        <TableCell className="text-right font-mono">{item.total_count?.toLocaleString()}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* By Date Table */}
                        {adminStats.by_date && adminStats.by_date.length > 0 && (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                {txt.byDate}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>{txt.date}</TableHead>
                                    <TableHead className="text-right">IP</TableHead>
                                    <TableHead className="text-right">ASN</TableHead>
                                    <TableHead className="text-right">{txt.country}</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {adminStats.by_date.map((item, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium">{item.date}</TableCell>
                                      <TableCell className="text-right font-mono font-bold">{item.ip_count?.toLocaleString()}</TableCell>
                                      <TableCell className="text-right font-mono text-muted-foreground">{item.asn_count}</TableCell>
                                      <TableCell className="text-right font-mono text-muted-foreground">{item.country_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{txt.noStatsData}</p>
                      <p className="text-sm mt-2">{language === 'tr' ? 'İstatistikleri yüklemek için yukarıdaki butona tıklayın.' : 'Click the button above to load statistics.'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* IP Search Tab - Admin Only */}
          {user?.admin === 1 && (
            <TabsContent value="ipsearch">
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Search className="h-5 w-5" />
                      {txt.ipSearch}
                    </CardTitle>
                    <CardDescription>
                      {language === 'tr' ? 'IP adresi veya prefix ile kayıtları ara' : 'Search records by IP address or prefix'}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Search Form */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Select value={ipSearchType} onValueChange={setIpSearchType}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder={txt.searchType} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ip">{txt.singleIp}</SelectItem>
                          <SelectItem value="prefix">{txt.prefixSearch}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex-1 min-w-[250px]">
                      <input
                        type="text"
                        value={ipSearchQuery}
                        onChange={(e) => setIpSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleIpSearch()}
                        placeholder={ipSearchType === 'ip' ? txt.ipPlaceholder : txt.prefixPlaceholder}
                        className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>

                    <Button 
                      onClick={handleIpSearch} 
                      disabled={ipSearchLoading || !ipSearchQuery.trim()}
                      className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                    >
                      {ipSearchLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      {ipSearchLoading ? txt.searching : txt.searchIp}
                    </Button>
                  </div>

                  {/* Error Message */}
                  {error && activeTab === 'ipsearch' && (
                    <Alert className="mb-6 bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <AlertDescription className="text-red-600 dark:text-red-400">{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Results */}
                  {ipSearchLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                    </div>
                  ) : ipSearchResults ? (
                    <div className="space-y-4">
                      {/* Results Header */}
                      <div className="flex items-center gap-4 mb-4">
                        <Badge variant="outline" className="text-sm">
                          {ipSearchResults.query_type === 'ip' ? txt.singleIp : txt.prefixSearch}: {ipSearchResults.query_value}
                        </Badge>
                      </div>

                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <Card className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20">
                          <CardContent className="pt-4 pb-4">
                            <div className="text-3xl font-bold text-blue-600">{ipSearchResults.total_records?.toLocaleString() || 0}</div>
                            <div className="text-sm text-muted-foreground">{txt.totalRecords}</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border-emerald-500/20">
                          <CardContent className="pt-4 pb-4">
                            <div className="text-3xl font-bold text-emerald-600">{ipSearchResults.total_active_records?.toLocaleString() || 0}</div>
                            <div className="text-sm text-muted-foreground">{txt.statusActive}</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border-red-500/20">
                          <CardContent className="pt-4 pb-4">
                            <div className="text-3xl font-bold text-red-600">{ipSearchResults.total_deleted_records?.toLocaleString() || 0}</div>
                            <div className="text-sm text-muted-foreground">{txt.statusDeleted}</div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Results Table */}
                      {ipSearchResults.records && ipSearchResults.records.length > 0 ? (
                        <div className="rounded-lg border border-border/50 overflow-hidden">
                          <div className="max-h-[500px] overflow-y-auto">
                            <Table>
                              <TableHeader className="sticky top-0 bg-muted/80 backdrop-blur">
                                <TableRow>
                                  <TableHead>{txt.ipAddress}</TableHead>
                                  <TableHead>{txt.nodeInfo}</TableHead>
                                  <TableHead>{txt.ports}</TableHead>
                                  <TableHead>ASN / {txt.org}</TableHead>
                                  <TableHead>{txt.country}</TableHead>
                                  <TableHead>{txt.firstSeen}</TableHead>
                                  <TableHead>{txt.lastSeen}</TableHead>
                                  <TableHead>{language === 'tr' ? 'Durum' : 'Status'}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {ipSearchResults.records.map((record, idx) => (
                                  <TableRow key={idx} className={record.status === 2 ? 'bg-red-500/5' : ''}>
                                    <TableCell className="font-mono font-medium">{record.ip_address}</TableCell>
                                    <TableCell>
                                      <div className="text-sm">
                                        <div className="font-medium">{record.node_name}</div>
                                        <div className="text-muted-foreground text-xs">{record.node_company}</div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-sm max-w-[150px] truncate" title={record.ports}>
                                      {record.ports || '-'}
                                    </TableCell>
                                    <TableCell>
                                      <div className="text-sm">
                                        <a 
                                          href={`https://bgp.tools/as/${record.asn}`} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-emerald-600 hover:underline font-mono"
                                        >
                                          AS{record.asn}
                                        </a>
                                        <div className="text-muted-foreground text-xs max-w-[150px] truncate" title={record.org}>
                                          {record.org}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <span className="mr-1">
                                        {record.iso ? String.fromCodePoint(...record.iso.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0))) : '🌐'}
                                      </span>
                                      {record.country}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                      {record.first_seen}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                      {record.last_seen}
                                    </TableCell>
                                    <TableCell>
                                      <Badge 
                                        variant={record.status === 1 ? 'default' : 'destructive'}
                                        className={record.status === 1 ? 'bg-emerald-500' : 'bg-red-500'}
                                      >
                                        {record.status === 1 ? txt.statusActive : txt.statusDeleted}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-muted-foreground">
                          <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>{txt.noResults}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>{language === 'tr' ? 'IP adresi veya prefix girin ve arayın.' : 'Enter an IP address or prefix and search.'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* IP Add Tab - Admin Only */}
          {user?.admin === 1 && (
            <TabsContent value="ipadd">
              <Card className="border-0 bg-gradient-to-br from-card to-muted/30">
                <CardHeader>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="h-5 w-5" />
                      {txt.ipAdd}
                    </CardTitle>
                    <CardDescription>{txt.ipAddDesc}</CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Mode Selection */}
                  <div className="mb-6">
                    <label className="text-sm font-medium mb-2 block">{txt.addMode}</label>
                    <Select value={ipAddMode} onValueChange={setIpAddMode}>
                      <SelectTrigger className="w-full max-w-xs">
                        <SelectValue placeholder={txt.addMode} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">{txt.singleIpMode}</SelectItem>
                        <SelectItem value="bulk">{txt.bulkIpMode}</SelectItem>
                        <SelectItem value="items">{txt.bulkItemsMode}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Single IP Mode */}
                  {ipAddMode === 'single' && (
                    <div className="space-y-4 max-w-lg">
                      <div>
                        <label className="text-sm font-medium mb-2 block">{txt.ipAddressLabel}</label>
                        <input
                          type="text"
                          value={ipAddSingleIp}
                          onChange={(e) => setIpAddSingleIp(e.target.value)}
                          placeholder="1.2.3.4"
                          className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">{txt.portsLabel}</label>
                        <input
                          type="text"
                          value={ipAddSinglePorts}
                          onChange={(e) => setIpAddSinglePorts(e.target.value)}
                          placeholder={txt.portsPlaceholder}
                          className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Bulk IPs Mode (Common Port) */}
                  {ipAddMode === 'bulk' && (
                    <div className="space-y-4 max-w-lg">
                      <div>
                        <label className="text-sm font-medium mb-2 block">{txt.ipsLabel}</label>
                        <textarea
                          value={ipAddBulkIps}
                          onChange={(e) => setIpAddBulkIps(e.target.value)}
                          placeholder={txt.ipsPlaceholder}
                          rows={6}
                          className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">{txt.portsLabel}</label>
                        <input
                          type="text"
                          value={ipAddBulkPorts}
                          onChange={(e) => setIpAddBulkPorts(e.target.value)}
                          placeholder={txt.portsPlaceholder}
                          className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Bulk Items Mode (Individual Ports) */}
                  {ipAddMode === 'items' && (
                    <div className="space-y-4 max-w-lg">
                      <div>
                        <label className="text-sm font-medium mb-2 block">{txt.itemsLabel}</label>
                        <textarea
                          value={ipAddItems}
                          onChange={(e) => setIpAddItems(e.target.value)}
                          placeholder={txt.itemsPlaceholder}
                          rows={8}
                          className="w-full px-4 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                        />
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="mt-6">
                    <Button 
                      onClick={handleIpAdd} 
                      disabled={ipAddLoading}
                      className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                    >
                      {ipAddLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      {ipAddLoading ? txt.adding : txt.addIps}
                    </Button>
                  </div>

                  {/* Error Message */}
                  {ipAddError && (
                    <Alert className="mt-6 bg-red-500/10 border-red-500/30">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <AlertDescription className="text-red-600 dark:text-red-400">{ipAddError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Success Result */}
                  {ipAddResult && (
                    <Alert className="mt-6 bg-emerald-500/10 border-emerald-500/30">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      <AlertDescription>
                        <div className="text-emerald-600 dark:text-emerald-400 font-medium mb-2">{txt.addSuccess}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                          <div className="text-center p-3 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">{ipAddResult.summary?.inserted_rows || 0}</div>
                            <div className="text-xs text-muted-foreground">{txt.insertedRows}</div>
                          </div>
                          <div className="text-center p-3 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-amber-600">{ipAddResult.summary?.updated_rows || 0}</div>
                            <div className="text-xs text-muted-foreground">{txt.updatedRows}</div>
                          </div>
                          <div className="text-center p-3 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">{ipAddResult.summary?.nodes?.length || 0}</div>
                            <div className="text-xs text-muted-foreground">{txt.affectedNodes}</div>
                          </div>
                          <div className="text-center p-3 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-emerald-600">{ipAddResult.summary?.unique_ips || 0}</div>
                            <div className="text-xs text-muted-foreground">{txt.uniqueIps}</div>
                          </div>
                        </div>
                        {ipAddResult.errors && ipAddResult.errors.length > 0 && (
                          <div className="mt-3 text-sm text-red-500">
                            {language === 'tr' ? 'Hatalar:' : 'Errors:'} {ipAddResult.errors.join(', ')}
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {user?.admin === 1 && (
            <TabsContent value="maint">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5" /> {language === 'tr' ? 'Liste Bakımı' : 'List Cleanup'}</CardTitle>
                  <CardDescription>{language === 'tr' ? 'Belirtilen günden beri tekrar görülmemiş IP\'leri listeden düşürür (status=2). Reconcile cron MikroTik\'ten de siler.' : 'Delist IPs not seen for the given number of days. The reconcile cron also removes them from MikroTik.'}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <Label>{language === 'tr' ? 'Gün' : 'Days'}</Label>
                      <Input type="number" min="1" value={pruneDays} onChange={(e) => setPruneDays(e.target.value)} className="w-28" />
                    </div>
                    <Button variant="outline" onClick={handlePrunePreview} disabled={pruneLoading}>
                      <Search className="h-4 w-4 mr-2" /> {language === 'tr' ? 'Önizle' : 'Preview'}
                    </Button>
                    {prunePreview && prunePreview.total > 0 && (
                      <Button variant="destructive" onClick={handlePruneExecute} disabled={pruneLoading}>
                        <Trash2 className="h-4 w-4 mr-2" /> {language === 'tr' ? `Sil (${prunePreview.total})` : `Delete (${prunePreview.total})`}
                      </Button>
                    )}
                  </div>

                  {pruneMsg && <Alert><AlertDescription>{pruneMsg}</AlertDescription></Alert>}

                  {prunePreview && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {language === 'tr' ? 'Silinecek benzersiz IP' : 'Unique IPs to delist'}: <strong>{prunePreview.total}</strong> · cutoff: {prunePreview.cutoff}
                      </p>
                      {prunePreview.sample?.length > 0 && (
                        <div className="border rounded-md max-h-80 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>IP</TableHead>
                                <TableHead>{language === 'tr' ? 'Son görülme' : 'Last seen'}</TableHead>
                                <TableHead>BGP</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {prunePreview.sample.map((r, i) => (
                                <TableRow key={i}>
                                  <TableCell className="font-mono">{r.target_ip}</TableCell>
                                  <TableCell>{r.last_seen}</TableCell>
                                  <TableCell>{Number(r.any_bgp) === 1 ? 'evet' : '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <Label>{language === 'tr' ? 'Tek IP listeden çıkar' : 'Delist single IP'}</Label>
                    <div className="flex gap-2 mt-1">
                      <Input placeholder="1.2.3.4" value={delistIp} onChange={(e) => setDelistIp(e.target.value)} className="max-w-xs font-mono" />
                      <Button variant="destructive" onClick={handleDelistIp}><Trash2 className="h-4 w-4 mr-2" />{language === 'tr' ? 'Çıkar' : 'Delist'}</Button>
                    </div>
                    {delistMsg && <p className="text-sm mt-2">{delistMsg}</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {user?.admin === 1 && (
            <TabsContent value="sentmail">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> {language === 'tr' ? 'Giden Mailler' : 'Sent Mails'}</CardTitle>
                  <CardDescription>{language === 'tr' ? 'AS bildirim maillerinin gönderim kaydı.' : 'Log of AS notification emails.'}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input placeholder={language === 'tr' ? 'ASN veya e-posta ara' : 'Search ASN or email'} value={mailSearch} onChange={(e) => setMailSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadMailLog(1)} className="max-w-xs" />
                    <Button variant="outline" onClick={() => loadMailLog(1)} disabled={mailLoading}><Search className="h-4 w-4 mr-2" />{language === 'tr' ? 'Ara' : 'Search'}</Button>
                    <Button variant="ghost" onClick={() => loadMailLog(mailLog?.page || 1)} disabled={mailLoading}><RefreshCw className={`h-4 w-4 ${mailLoading ? 'animate-spin' : ''}`} /></Button>
                  </div>

                  {mailLog && (
                    <>
                      <div className="border rounded-md overflow-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{language === 'tr' ? 'Tarih/Saat' : 'Date/Time'}</TableHead>
                              <TableHead>ASN</TableHead>
                              <TableHead>{language === 'tr' ? 'E-posta' : 'Email'}</TableHead>
                              <TableHead>IP</TableHead>
                              <TableHead>{language === 'tr' ? 'Tip' : 'Type'}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {mailLog.rows.map((r) => (
                              <TableRow key={r.id}>
                                <TableCell className="whitespace-nowrap">{r.sent_at}</TableCell>
                                <TableCell className="font-mono">
                                  AS{r.asn}
                                  {(r.geo_org || r.geo_country_name) && (
                                    <span className="block text-xs text-muted-foreground font-sans">{[r.geo_org, r.geo_country_name].filter(Boolean).join(' · ')}</span>
                                  )}
                                </TableCell>
                                <TableCell>{r.email}</TableCell>
                                <TableCell>{r.ip_count}</TableCell>
                                <TableCell><Badge variant="outline">{r.mail_type}</Badge></TableCell>
                              </TableRow>
                            ))}
                            {mailLog.rows.length === 0 && (
                              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{language === 'tr' ? 'Kayıt yok' : 'No records'}</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'tr' ? 'Toplam' : 'Total'}: {mailLog.total} · {language === 'tr' ? 'Sayfa' : 'Page'} {mailLog.page}/{mailLog.total_pages || 1}</span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={mailLog.page <= 1 || mailLoading} onClick={() => loadMailLog(mailLog.page - 1)}>{language === 'tr' ? 'Önceki' : 'Prev'}</Button>
                          <Button variant="outline" size="sm" disabled={mailLog.page >= (mailLog.total_pages || 1) || mailLoading} onClick={() => loadMailLog(mailLog.page + 1)}>{language === 'tr' ? 'Sonraki' : 'Next'}</Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {user?.admin === 1 && (
            <TabsContent value="ddos">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><DdosIcon className="h-5 w-5" /> Spoof / DDoS</CardTitle>
                  <CardDescription>{language === 'tr' ? 'Agent\'ın tespit ettiği spoof/reflection/DDoS olayları (listeye eklenmez, yalnız analiz).' : 'Spoof/reflection/DDoS events detected by the agent (not listed, analysis only).'}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Input placeholder={language === 'tr' ? 'IP / port / node ara' : 'Search IP / port / node'} value={ddosSearch} onChange={(e) => setDdosSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadDdos(1)} className="max-w-xs" />
                    <select value={ddosSpoofed} onChange={(e) => setDdosSpoofed(e.target.value)} className="border rounded-md px-2 text-sm bg-transparent">
                      <option value="">{language === 'tr' ? 'Spoof: Hepsi' : 'Spoof: All'}</option>
                      <option value="1">Spoofed</option>
                      <option value="0">{language === 'tr' ? 'Doğrulanmış' : 'Verified'}</option>
                    </select>
                    <select value={ddosProto} onChange={(e) => setDdosProto(e.target.value)} className="border rounded-md px-2 text-sm bg-transparent">
                      <option value="">{language === 'tr' ? 'Proto: Hepsi' : 'Proto: All'}</option>
                      <option value="TCP">TCP</option>
                      <option value="UDP">UDP (reflection)</option>
                    </select>
                    <Button variant="outline" onClick={() => loadDdos(1)} disabled={ddosLoading}><Search className="h-4 w-4 mr-2" />{language === 'tr' ? 'Ara' : 'Search'}</Button>
                  </div>

                  {ddosLog && (
                    <>
                      <div className="border rounded-md overflow-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{language === 'tr' ? 'Zaman' : 'Time'}</TableHead>
                              <TableHead>{language === 'tr' ? 'Tip' : 'Type'}</TableHead>
                              <TableHead>Proto</TableHead>
                              <TableHead>Port</TableHead>
                              <TableHead>{language === 'tr' ? 'Kaynak' : 'Source'}</TableHead>
                              <TableHead>pps</TableHead>
                              <TableHead>Node</TableHead>
                              <TableHead>Spoof</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {ddosLog.rows.map((r) => (
                              <TableRow key={r.id}>
                                <TableCell className="whitespace-nowrap text-xs">{r.created_at}</TableCell>
                                <TableCell>{r.flood_type}</TableCell>
                                <TableCell>{r.proto}</TableCell>
                                <TableCell className="font-mono">{r.target_port}</TableCell>
                                <TableCell className="font-mono text-xs align-top max-w-[280px]">
                                  {r.src_ip ? r.src_ip : (
                                    <>
                                      {r.unique_sources ? <span className="text-muted-foreground">{r.unique_sources} {language === 'tr' ? 'kaynak' : 'src'}</span> : '-'}
                                      {(() => { let ips = []; try { ips = JSON.parse(r.sample_ips || '[]'); } catch (e) { /* */ } return ips.length ? <span className="block text-[10px] break-all mt-0.5">{ips.join(', ')}</span> : null; })()}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell>{r.packets_per_second || '-'}</TableCell>
                                <TableCell className="text-xs">{r.node_id}</TableCell>
                                <TableCell>{Number(r.spoofed) === 1 ? <Badge variant="destructive">spoofed</Badge> : <Badge variant="outline">ok</Badge>}</TableCell>
                              </TableRow>
                            ))}
                            {ddosLog.rows.length === 0 && (
                              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">{language === 'tr' ? 'Kayıt yok' : 'No records'}</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{language === 'tr' ? 'Toplam' : 'Total'}: {ddosLog.total} · {language === 'tr' ? 'Sayfa' : 'Page'} {ddosLog.page}/{ddosLog.total_pages || 1}</span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={ddosLog.page <= 1 || ddosLoading} onClick={() => loadDdos(ddosLog.page - 1)}>{language === 'tr' ? 'Önceki' : 'Prev'}</Button>
                          <Button variant="outline" size="sm" disabled={ddosLog.page >= (ddosLog.total_pages || 1) || ddosLoading} onClick={() => loadDdos(ddosLog.page + 1)}>{language === 'tr' ? 'Sonraki' : 'Next'}</Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* AS Modal - Pure white, no shadows, no borders */}
      <SimpleModal 
        open={asModalOpen} 
        onClose={() => { setAsModalOpen(false); resetAsModal(); }}
        title={asStep === 1 ? txt.step1Title : txt.step2Title}
        subtitle={asStep === 1 ? txt.step1Desc : txt.step2Desc}
      >
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded">
              {error}
            </div>
          )}

          {asStep === 1 ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.asNumber}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mono text-gray-500">AS</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="12345"
                  maxLength={6}
                  value={newAsn}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/\D/g, '');
                    setNewAsn(value);
                  }}
                  className="w-full pl-10 pr-3 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.abuseEmail}</label>
                <Select value={selectedAbuseEmail} onValueChange={setSelectedAbuseEmail}>
                  <SelectTrigger className="bg-white dark:bg-zinc-800">
                    <SelectValue placeholder={language === 'tr' ? 'E-posta seçin...' : 'Select email...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {abuseContacts.map((contact, i) => (
                      <SelectItem key={i} value={contact.email}>
                        {contact.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedAbuseEmail && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  {language === 'tr' 
                    ? `Doğrulama linki ${selectedAbuseEmail} adresine gönderilecek.`
                    : `Verification link will be sent to ${selectedAbuseEmail}.`
                  }
                </p>
              )}
            </>
          )}

          {asStep === 1 ? (
            <button 
              onClick={handleFetchAbuseContacts} 
              disabled={isLoading || !newAsn}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium rounded transition-colors"
            >
              {isLoading ? 'Yükleniyor...' : txt.fetchContacts}
            </button>
          ) : (
            <div className="flex gap-2">
              <button 
                onClick={() => setAsStep(1)} 
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 font-medium rounded transition-colors"
              >
                {txt.back}
              </button>
              <button 
                onClick={handleAddAS} 
                disabled={isLoading || !selectedAbuseEmail}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium rounded transition-colors"
              >
                {isLoading ? 'Yükleniyor...' : txt.sendVerification}
              </button>
            </div>
          )}
        </div>
      </SimpleModal>

      {/* BGP Modal - Pure white, no shadows, no borders */}
      <SimpleModal 
        open={bgpModalOpen} 
        onClose={() => { setBgpModalOpen(false); resetBgpModal(); }}
        title={txt.addBgp}
        subtitle={language === 'tr' ? 'Yeni bir BGP oturumu yapılandırın' : 'Configure a new BGP session'}
      >
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.selectAs}</label>
            <Select value={selectedAsForBgp} onValueChange={setSelectedAsForBgp}>
              <SelectTrigger className="bg-white dark:bg-zinc-800">
                <SelectValue placeholder={language === 'tr' ? 'AS seçin...' : 'Select AS...'} />
              </SelectTrigger>
              <SelectContent>
                {verifiedAsList.map((as) => (
                  <SelectItem key={as.asn_id} value={as.asn_id}>
                    {as.asn} ({as.bgp_sessions_count}/{as.max_sessions})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.sessionName}</label>
            <input
              type="text"
              value={bgpForm.session_name} 
              onChange={handleSessionNameChange}
              maxLength={17}
              placeholder={language === 'tr' ? 'Örn: Primary BGP' : 'e.g., Primary BGP'}
              className="w-full px-3 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'tr' ? 'Türkçe karakter kullanılamaz' : 'Turkish characters not allowed'}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.remoteIp}</label>
            <input
              type="text"
              value={bgpForm.remote_ip} 
              onChange={handleRemoteIPChange} 
              placeholder="192.168.1.100"
              className="w-full px-3 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'tr' ? 'Sizin Router IP Adresiniz' : 'Your Router IP Address'}
            </p>
          </div>

          <button 
            onClick={handleAddBGP} 
            disabled={isLoading || !selectedAsForBgp || !bgpForm.session_name || !bgpForm.remote_ip}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium rounded transition-colors"
          >
            {isLoading ? 'Yükleniyor...' : txt.create}
          </button>
        </div>
      </SimpleModal>

      {/* Webhook Modal - Pure white, no shadows, no borders */}
      <SimpleModal 
        open={webhookModalOpen} 
        onClose={() => setWebhookModalOpen(false)}
        title={txt.addWebhook}
        subtitle={language === 'tr' ? 'Yeni IP bildirimleri için webhook ekleyin' : 'Add a webhook for new IP notifications'}
      >
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.webhookUrl}</label>
            <input
              type="text"
              placeholder="https://example.com/webhook"
              value={newWebhookUrl} 
              onChange={(e) => setNewWebhookUrl(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-xs text-gray-400">{txt.webhookHttpsRequired}</p>
          </div>

          <button 
            onClick={handleAddWebhook} 
            disabled={isLoading || !newWebhookUrl}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium rounded transition-colors"
          >
            {isLoading ? 'Yükleniyor...' : txt.create}
          </button>
        </div>
      </SimpleModal>

      {/* Webhook Detail Modal */}
      <SimpleModal 
        open={webhookDetailModal.open} 
        onClose={() => setWebhookDetailModal({ open: false, webhook: null })}
        title={txt.webhookDetails}
        subtitle={webhookDetailModal.webhook?.webhook_url}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.lastTriggered}</label>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {webhookDetailModal.webhook?.last_triggered_at || txt.never}
            </p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{txt.lastResponse}</label>
            <div className="bg-gray-50 dark:bg-zinc-800 rounded p-3 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words font-mono">
                {webhookDetailModal.webhook?.last_response || '-'}
              </pre>
            </div>
          </div>
          
          <button 
            onClick={() => setWebhookDetailModal({ open: false, webhook: null })}
            className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 font-medium rounded transition-colors"
          >
            {language === 'tr' ? 'Kapat' : 'Close'}
          </button>
        </div>
      </SimpleModal>

      <Footer />
    </div>
  );
};

export default DashboardPage;
